import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import "./icons.scss";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store, { persistor } from "./redux-store";
// Side-effect module: wires @rista/core's store + effects bridges. Must run before any
// shared logic issues a request. See bootstrapCoreBridges.ts.
import "./bootstrapCoreBridges";
import { PersistGate } from "redux-persist/integration/react";
import Routes from "./Routes";
import history from "./history";
import CustomRouter from "./CustomRouter";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ToastContainer } from "react-toastify";
import { ToastHost } from "@rista/ui/library/components/Toast";
import { AlertHost } from "@rista/ui/library/components/AlertDialog";
import { IconAssetsProvider } from "@rista/ui/library/icons/IconAssets";
import spriteSheet from "@rista/ui/library/icons/sheet/rista-sprite-3x.png";

/** v2's icon pictures (DESIGN_SYSTEM.md §5.1, QD9): the one sprite sheet, which webpack turns into a URL */
const ICON_ASSETS = { sprite: { uri: spriteSheet } };
import "react-toastify/dist/ReactToastify.css";
import 'core-js/actual';
// Side-effect module: subscribes to RequestManager once at app startup
import './utils/requestManager/globalInteractionLock' // Do not remove this import as it sets up global interaction lock based on RequestManager's state
import './utils/requestManager/globalLoaderReduxSync'; // Side-effect module: syncs RequestManager's state with Redux global loader state
import * as Sentry from "@sentry/react";
import { getCurrentVersions } from "helper/commonHelpers";
import { initializeGoogleAnalytics } from "helper/ga-helper";
import { getLocalStorage, removeLocalStorage } from "utils";
import { REDUX_PERSIST_STATE } from "utils/constants";
import { setupGlobalErrorHandlers } from "utils/globalErrorHandler";
import { GlobalLoader } from "commonComponents/GlobalLoader";
import { isAndroid } from "utils/nativeHooks";
import { isCustomerDisplay } from "utils/customerDisplay";
import CustomerDisplay from "components/CustomerDisplay";
import { LOST_DATA_DEVICE, MIGRATION_COMPLETED, OLD_STATE_KEY } from "utils/migrateState";
import { allowBrowserDevAccess, currentAppEnv, isBetaEnv, isProdEnv, isStagingEnv } from "data/env-constants";

declare global {
  interface Window {
    ReactNativeWebView: any;
    electronAPI?: {
      isElectron: true;
      platform: string;
      shellVersion: string;
      reactVersion: string;
      env?: "dev" | "beta" | "prod";
      notifyReady: () => void;
      checkForUpdates: () => void;
      exec: (key: string, action: string, args?: unknown) => Promise<any>;
      /** OS-level app lockdown — driven only by @rista/core/utils/deviceLock. */
      deviceLock?: { setLock: (locked: boolean) => Promise<boolean> };
      printer: {
        print: (
          content: any,
          printerVo: any,
          printProps?: any,
        ) => Promise<{ success?: 'OK'; error?: string }>;
        discoverLocalDevices:     () => Promise<{ success?: any[]; error?: string }>;
        discoverBluetoothDevices: () => Promise<{ error: string }>;
        discoverUSBDevices:       () => Promise<{ success?: any[]; error?: string }>;
        // Deprecated — back-compat shim removed in Phase 4 along with electronAPI.exec.
        exec: (action: string, message: any) => Promise<any>;
        onResult: (cb: (data: any) => void) => any;
        off: (channel: string, ref: any) => void;
      };
      push: {
        register: (config: any) => void;
        unregister: () => void;
        onNotification: (cb: (data: any) => void) => any;
        onRegistration: (cb: (data: any) => void) => any;
        off: (channel: 'push:notification' | 'push:registration', ref: any) => void;
      };
      server: {
        start: (data: any) => Promise<any>;
        stop: () => Promise<any>;
        status: () => Promise<{ isRunning: boolean; err?: any }>;
        getIpV4: () => Promise<Array<{ name: string; address: string }>>;
        post: (data: any) => Promise<{ data?: any; error?: string }>;
        setOffline: (isOffline: boolean) => void;
        setDeviceInfo: (deviceInfo: any) => void;
        setPrinterConfig: (config: any) => void;
        onNetworkRequest: (cb: (data: any) => void) => any;
        onError: (cb: (data: any) => void) => any;
        off: (channel: 'server:networkrequest' | 'server:error', ref: any) => void;
      };
      cartAudit: {
        setToken: (payload: { token: string; baseUrl?: string }) => void;
      };
      // Customer display (second monitor). open/close/cart/ready go through the
      // generic exec shim as 'chrome.dualscreen'; only the push needs a listener.
      customerDisplay: {
        onCart: (cb: (data: any) => void) => any;
        off: (ref: any) => void;
      };
      // Unified updates namespace. Top level contains only listeners that BOTH
      // tracks (shell + OTA) actually emit. Track-specific listeners and
      // lifecycle methods live under .shell.* and .ota.*. Every event payload
      // carries `track: 'shell' | 'ota'` so the renderer can filter.
      updates: {
        // Shared listeners (both tracks emit)
        onUpdateDownloaded: (
          cb: (payload: { track: 'shell' | 'ota'; version: string; isForcedUpdate?: boolean }) => void,
        ) => any;
        onUpdateError: (
          cb: (payload: {
            track:    'shell' | 'ota';
            message:  string;
            code?:    string;
            version?: string;
            origin?:  string;
          }) => void,
        ) => any;
        off: (channel: string, ref: any) => void;

        // Shell-only — richer event lifecycle + post-check controls
        shell: {
          // Listeners (OTA never emits on these channels)
          onUpdateAvailable: (
            cb: (payload: {
              track:                'shell';
              version:              string;
              isForcedUpdate:       boolean;
              message:              string;
              requiresConfirmation: boolean;
            }) => void,
          ) => any;
          onDownloadStarted: (cb: (payload: { track: 'shell' }) => void) => any;
          onUpdateProgress: (
            cb: (payload: {
              track:           'shell';
              percentage:      number;
              bytesPerSecond:  number;
              total:           number;
              transferred:     number;
            }) => void,
          ) => any;
          onUpdateNotAvailable: (
            cb: (payload: { track: 'shell'; version?: string }) => void,
          ) => any;

          // Lifecycle methods
          checkForUpdate: (
            creds: { deviceIdentifier: string; atoken: string },
          ) => Promise<{ ok: boolean; reason?: string }>;
          startUpdate:      () => Promise<{ ok: boolean; reason?: string }>;
          applyUpdate:      () => Promise<{ ok: boolean; reason?: string }>;
          isAlreadyUpdated: () => Promise<{ updated: boolean }>;
          cleanState:       () => Promise<{ ok: boolean }>;
        };

        // OTA-only — no manual check from renderer; the only action is relaunching
        // to apply the staged build (preload fires the actual check at launch).
        ota: {
          restartToUpdate: () => void;
        };
      };
    };
    ristaDB?: {
      get: (domain: string, tid: string, keys: string[] | null) => Promise<any[]>;
      replace: (domain: string, tid: string, records: any[]) => Promise<any>;
      put: (domain: string, tid: string, record: any, isOffline: boolean) => Promise<any>;
      delete: (domain: string, tid: string, keys: string[] | null) => Promise<any>;
    };
  }
}


function getMetaInfo() {
  const branchId = store.getState()?.businessStore?.businessData?.branchId || "";
  const accountUUID = store.getState()?.businessStore?.businessData?.accountUUID || "";
  const userId = store.getState()?.businessStore?.userData?.userId || "";
  const businessUserId = store.getState()?.businessStore?.userData?.businessUserId || "";
  const appVersions = getCurrentVersions();
  const nwAppVersion = appVersions.nwAppVersion || "web";
  const oldStateStr = localStorage.getItem(OLD_STATE_KEY);
  return {
    branchId,
    userId,
    businessUserId,
    accountUUID,
    ...appVersions,
    nwAppVersion,
    oldStateExists: !!oldStateStr,
  };
}

// The customer display is a passive second screen, not a user session — GA
// would record it as a phantom parallel session for every order.
if (!isCustomerDisplay()) {
  initializeGoogleAnalytics();
}

const versionInfo = getCurrentVersions();
export const replay = Sentry.replayIntegration();
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: currentAppEnv,
  release: versionInfo.oracleVersion,
  integrations: [
    replay,
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
  ],
  tracePropagationTargets: ["*"],
  tracesSampleRate: isProdEnv ? 1 : 1, // transaction capture %
  replaysSessionSampleRate: isProdEnv ? 0 : 0.1, // sample rate of replay
  replaysOnErrorSampleRate: 0, // sampling rate when error occurs,
  profilesSampleRate: isProdEnv ? 0.25 : 1,
  beforeSend(event) {
    const metaInfo = getMetaInfo();
    event.tags = { ...(event.tags || {}), ...metaInfo };
    return event;
  },
  beforeSendTransaction(event) {
    const metaInfo = getMetaInfo();
    event.tags = { ...(event.tags || {}), ...metaInfo };
    return event;
  },
});

setupGlobalErrorHandlers();

const root = ReactDOM.createRoot(document.getElementById("root")!);

const fallbackComponent = () => {

  function handleReload() {
    window.location.reload();
  }

  return (
     <div className="h-[100vh] w-[100vw] flex justify-center items-center">
      <p>Failed to load. Please refresh.</p>
      <div
        className={`rista-sprite blue-refresh-copy rista-new-sprite cursor-pointer scale-125 ml-4`}
        onClick={() => handleReload()}
      ></div>
    </div>
  )
};

const AppWithProvider = () => {
  return (
    <Sentry.ErrorBoundary fallback={fallbackComponent()}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <CustomRouter history={history}>
            <IconAssetsProvider value={ICON_ASSETS}>
            <ToastContainer theme="dark" />
            {/* v2's toast (@rista/core/toast), in the same design; v1's container above is unchanged */}
            <ToastHost />
            {/* v2's alert (@rista/core/alert), in v1's warning modal's design */}
            <AlertHost />
            <Routes />
            <GlobalLoader />
            </IconAssetsProvider>
          </CustomRouter>
        </PersistGate>
      </Provider>
    </Sentry.ErrorBoundary>
  );
}
const AppWithProfiler = Sentry.withProfiler(AppWithProvider);

// Mutates migration flags in localStorage, which the display window shares with
// the POS window — only the POS window may run it.
if (!isCustomerDisplay()) {
  checkOldStateExists();
}

// clearReduxOldState();

// The customer display mounts a bare read-only tree: no router, no PersistGate,
// no auth. Everything it renders arrives over the shell's cart push, so it must
// not go through Routes (which would put it behind the login redirect with an
// empty store of its own).
// #root has no explicit height anywhere (no global CSS sets it), so a bare
// render here leaves CustomerDisplay's own height:"100%" chain unresolved:
// width-based splits (landscape) still work since block elements default to
// full viewport width, but height-based splits (portrait) collapse to
// content-sized boxes instead of the configured media/cart ratio.
//
// `allowBrowserDevAccess` is the sanctioned local-dev bypass of the access-denied
// screen — set REACT_APP_ALLOW_BROWSER_DEV in .env.local rather than editing this
// condition. It is double-gated on the LOCAL env, so it cannot be true in a
// deployed build.
isCustomerDisplay()
  ? root.render(
      <div style={{ width: "100vw", height: "100vh" }}>
        <CustomerDisplay />
      </div>,
    )
  : window.top === window.self && !(isProdEnv || isBetaEnv || isStagingEnv || isAndroid() || !!window.electronAPI?.isElectron || allowBrowserDevAccess)
  ? root.render(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>☠️</div>
        <h1
          style={{ fontSize: "2rem", marginBottom: "1rem", color: "#d32f2f" }}
        >
          Access Denied
        </h1>
        <p style={{ fontSize: "1.2rem", color: "#555" }}>
          Please use the desktop application.
        </p>
      </div>
    )
  : root.render(<AppWithProfiler />);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
if (!window.electronAPI?.isElectron && !isCustomerDisplay()) {
  serviceWorkerRegistration.register();
}
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
// register();

// Whole-sale parity capture (docs/flows/PAYMENT_FLOW_V2.md §14b). Dynamic so it is
// absent from a production bundle, and inert until armed with
// `localStorage.setItem('PAYMENT_CAPTURE', '1')`.
if (process.env.NODE_ENV !== "production") {
  import("./payments/commitCapture").then((m) => m.installCommitCapture());
}

function checkOldStateExists() {
  const oldStateStr = localStorage.getItem(OLD_STATE_KEY);
  if (oldStateStr) {
    removeLocalStorage(MIGRATION_COMPLETED);
    removeLocalStorage(LOST_DATA_DEVICE);

  }
  return !!oldStateStr;
}

function clearReduxOldState() {
  const hasReduxPersistState = !!getLocalStorage(REDUX_PERSIST_STATE);
  if (hasReduxPersistState) {
    localStorage.removeItem(OLD_STATE_KEY); // Clear the old state key if it exists
  }
}